import { useEffect, useState } from "react";
import ApiConnector from "../../connector/ApiConnector";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons/faTriangleExclamation";

const efficiencyColor = (pct) => {
  if (pct == null) return "#888";
  if (pct >= 80) return "#2e7d32";
  if (pct >= 50) return "#f57c00";
  return "#c62828";
};

const ScoreTable = ({ score }) => {
  if (!score) return <p style={{ color: "#888", fontSize: "0.9em" }}>Manual config — score not available</p>;
  return (
    <table className="config-table">
      <tbody>
        <tr><td>TP</td><td>{score.truePositives}</td></tr>
        <tr><td>FP</td><td>{score.falsePositives}</td></tr>
        <tr><td>Effective TP</td><td>{score.effectiveTruePositives}</td></tr>
        <tr><td>Effective FP</td><td>{score.effectiveFalsePositives}</td></tr>
        <tr><td>Effective ratio</td><td>{score.effectiveRatio?.toFixed(2)}</td></tr>
      </tbody>
    </table>
  );
};

const Component = ({ onRevert }) => {
  const [data, setData] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [reverting, setReverting] = useState(false);

  useEffect(() => {
    ApiConnector.getConfigEfficiency().then((d) => {
      setData(d);
      setSelectedIdx(0);
    }).catch(() => setData(null));
  }, []);

  const handleRevert = async () => {
    if (!data || !selected) return;
    const d = new Date(selected.configCreated);
    const dateStr = `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
    const confirmed = window.confirm(
      `Revert to benchmark config #${selected.configId} from ${dateStr}?\n\nThis will replace the current config with the selected benchmark config.`
    );
    if (!confirmed) return;
    setReverting(true);
    try {
      await ApiConnector.revertToBenchmarkConfig(selected.configId);
      if (onRevert) onRevert();
      ApiConnector.getConfigEfficiency().then(setData);
    } finally {
      setReverting(false);
    }
  };

  if (!data || !data.benchmarkCandidates?.length) return (
    <p style={{ color: "#888", fontSize: "0.9em" }}>Not enough simulation data available yet.</p>
  );

  const selected = data.benchmarkCandidates[selectedIdx];
  const pct = selected.efficiencyPercent != null ? Math.round(selected.efficiencyPercent) : null;
  const benchmarkDate = selected.configCreated
    ? new Date(selected.configCreated).toLocaleDateString()
    : "—";

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
        <span style={{ fontSize: "1.5em", fontWeight: "bold", color: efficiencyColor(pct) }}>
          {pct != null ? `${pct}%` : "N/A"}
        </span>
        <span style={{ color: "#888", fontSize: "0.9em" }}>
          efficiency vs benchmark config #{selected.configId} ({benchmarkDate})
        </span>
      </div>

      {data.benchmarkCandidates.length > 1 && (
        <div style={{ marginBottom: "12px" }}>
          <label style={{ marginRight: "8px", fontSize: "0.9em" }}>Compare with:</label>
          <select
            value={selectedIdx}
            onChange={(e) => setSelectedIdx(Number(e.target.value))}
            style={{ width: "90%" }}
          >
            {data.benchmarkCandidates.map((c, i) => {
              const cd = new Date(c.configCreated);
              const dateStr = `${cd.getDate()}.${cd.getMonth() + 1}.${cd.getFullYear()}`;
              return (
                <option key={c.configId} value={i}>
                  Config #{c.configId} · {dateStr} · ratio: {c.score?.effectiveRatio?.toFixed(2)}
                </option>
              );
            })}
          </select>
        </div>
      )}

      <div style={{ display: "flex", gap: "15px", marginBottom: "0" }}>
        <div>
          <strong>Benchmark config #{selected.configId}</strong>
          <ScoreTable score={selected.score} />
          <button onClick={handleRevert} disabled={reverting} style={{ marginTop: "0px" }}>
            {reverting ? "Reverting..." : `Revert to config #${selected.configId}`}
          </button>
        </div>
        <div>
          <strong>Current config #{data.currentConfigId}</strong>
          <ScoreTable score={data.currentScore} />
          {pct != null && pct < 100 && (
            <p style={{ color: "#c62828", fontSize: "0.85em", marginTop: "8px" }}>
              <FontAwesomeIcon icon={faTriangleExclamation} className="fa-fade" />{" "}
              Warning: lower performance
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Component;
